import React, {
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import {
  DropDownLabel,
  DropdownList,
  DropdownListGroup,
  LoadingIndicator,
  PolyIcon
} from "elements";
import { useTranslation } from "utilities/i18n";
import { addOrRemove, move } from "utilities/arrayUtils";
import { nameIsUnassigned } from "utilities/stringUtils";
import { useUserMeta } from "utilities/userMeta";
import { sanitizeSearchInput } from "utilities/search";
import { useQueryParamString } from "polyrouter";
import _ from "lodash";
import { DATE_FORMAT, MAX_SEARCH_CHARS } from "Constants";
import { useSelector } from "react-redux";
import { State } from "store";
import { SortDirection, SortState } from "store/filters";
import { TABLE_COLUMN_STATE } from "store/userMeta";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from "react-beautiful-dnd";
import { SiteDetails } from "routes/Manage/Sites/List/SiteList";
import { RoomDetails } from "routes/Manage/Rooms/List/RoomsList";
import { PayloadAC } from "typesafe-actions";
import { isValid, parse } from "date-fns";
import VisibilitySensor from "react-visibility-sensor";

interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  loading: boolean;
  loaderClassName?: string;
  cols: number;
  className?: string;
  accommodateScrollbar?: boolean;
}

interface TableBulkItemRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  header?: boolean;
  children: any;
  className?: string;
  checked: boolean;
  onCheck: () => void;
  replaceCheckWith?: ReactNode;
  showChecks?: boolean;
  singleSelection?: boolean;
  onClearFilters?: () => void;
  showClearFilters?: boolean;
  showClearFiltersHeader?: boolean;
  "data-testid"?: string;
  nested?: boolean;
}

interface SortProps {
  currentSort: SortState;
  onSort: (sort: SortState) => void;
}

interface FilterProps {
  filterValue: string;
  filterOptions: (string | { text: string; value: string })[];
  onFilter: (field: string, value: string) => void;
}

export interface TableColumn {
  key: string;
  title: string;
}

export interface TableHeaderProps {
  fieldName: string;
  leftTitle?: ReactNode;
  title: string;
  rightTitle?: ReactNode;
  className?: string;
  dataTestId?: string;
  sort?: SortProps;
  filter?: FilterProps;
}

interface TableSearchProps {
  onSearch:
    | PayloadAC<"UpdateSearch", string | undefined>
    | ((searchTerm: string | undefined) => void);
  searchPlaceholder?: string;
  evaluateOnDemand?: boolean;
  debouncePeriod?: number;
}

export interface TableEditProps {
  tableName: string;
  shownColumns: Array<TableColumn>;
  hiddenColumns: Array<TableColumn>;
}

export const HEADER_ID = "HEADER_ID";

/**
 * Checks if the item is "checked" or not within the passed-in "set"
 */
export const itemCheckedWithSet = (items: Array<string>) => {
  // Convert items to a set since just checking whether an item exists or not
  const itemSet = new Set(items);
  return (id: string) => {
    return itemSet.has(id);
  };
};

/**
 * Check one item only (and uncheck all others)
 * function to be used in onChange for table rows in order to
 * have the table only have one item checked.
 */
export const toggleItemSingleOnlyWithSet = (
  items: Array<string>,
  setItems: (items: Array<string>) => void
) => (id: string) => {
  const previouslyChecked = itemCheckedWithSet(items);

  // uncheck if previously checked
  if (previouslyChecked(id)) {
    setItems([]);

    return;
  }

  setItems([id]);
};

/**
 * function to be used in the onChange for table rows in
 * order to have the table track multiple checked items.
 */
export const toggleItemWithSet = (
  itemCount: number,
  items: Array<string>,
  setItems: (items: Array<string>) => void,
  ignoreId = ""
) => (id: string) => {
  const itemChecked = itemCheckedWithSet(items);
  if (itemChecked(id)) {
    setItems(
      items.filter(
        item => item !== HEADER_ID && item !== id && item !== ignoreId
      )
    );
    return;
  }

  // if all items are checked, check the header box as well
  if (items.length >= itemCount - 1) {
    setItems([...items.filter(i => i !== ignoreId), id, HEADER_ID]);
    return;
  }

  setItems([...items.filter(i => i !== ignoreId), id]);
};

export const selectItems = (
  sourceItems: Array<string>,
  targetItems: Array<string>,
  setItems: (items: Array<string>) => void,
  ignoreIds: Array<string> = []
) => {
  let newSourceItems;
  if (ignoreIds) {
    const ignoreIdSet = new Set(ignoreIds);
    newSourceItems = sourceItems.filter(i => !ignoreIdSet.has(i));
  } else {
    newSourceItems = sourceItems;
  }

  const targetItemSet = new Set(targetItems);

  const newItems = newSourceItems.filter(i => targetItemSet.has(i));
  if (newItems.length >= newSourceItems.length) {
    newItems.push(HEADER_ID);
  }
  setItems(newItems);
};

/**
 * function to be used in the table header row to allow the
 * checkbox in the header to check all items in the table.
 */
export const toggleItems = (
  sourceItems: Array<string>,
  targetItems: Array<string>,
  checkedItems: Array<string>,
  setItems: (items: Array<string>) => void,
  ignoreIds: Array<string> = []
) => {
  let newSourceItems;
  if (ignoreIds) {
    const ignoreIdSet = new Set(ignoreIds);
    newSourceItems = sourceItems.filter(i => !ignoreIdSet.has(i));
  } else {
    newSourceItems = sourceItems;
  }

  const targetItemSet = new Set(targetItems);
  const checkedItemSet = new Set(checkedItems);

  let toggle: boolean;
  if (targetItems.length > 0 && checkedItems.length > 0) {
    const hasSelectedNonTargetItems =
      newSourceItems.findIndex(
        i => checkedItemSet.has(i) && !targetItemSet.has(i)
      ) >= 0;

    if (hasSelectedNonTargetItems) {
      toggle = false;
    } else {
      toggle = targetItems.findIndex(i => !checkedItemSet.has(i)) < 0;
    }
  } else {
    toggle = true;
  }

  // Toggle values
  const newItems = newSourceItems.filter(
    i =>
      targetItems.length < 1 ||
      (targetItemSet.has(i) && (!toggle || !checkedItemSet.has(i)))
  );

  if (newItems.length >= newSourceItems.length) {
    newItems.push(HEADER_ID);
  }
  setItems(newItems);
};

export const toggleAll = (
  items: Array<string>,
  checked: Array<string>,
  setItems: (items: Array<string>) => void,
  ignoreId = ""
) => {
  const isChecked = itemCheckedWithSet(checked);
  const checkedItems = isChecked(HEADER_ID)
    ? []
    : [...items.filter(i => i !== ignoreId), HEADER_ID];
  setItems(checkedItems);
};

/**
 * Convenience function to get all of the items excluding the header checkbox
 */
export const getSelectedWithoutHeader = (items: Array<string>) =>
  items.filter(item => item !== HEADER_ID);

/**
 * *****************
 * !!! IMPORTANT !!!
 * *****************
 *
 * BEFORE using this, be absolutely confident that the data for the table
 * will NEVER EVER exceed 100 records.
 *
 * 100 records is the "default" paging size for most queries.
 * If there are more than 100 records, then this sorting will NOT work properly,
 * and instead you should use server-side sorting (see TableView.tsx for an
 * example of how to implement server-side sorting)
 */
export function sortTableData<T>(items: Array<T>, sort: SortState) {
  const field = sort.field as keyof T;
  if (!field) {
    return;
  }

  // TODO: direction does not appear to be used in all relevant places?
  const direction = sort.direction === "ASC" ? 1 : -1;

  items.sort((a: T, b: T) => {
    // always sort empty (null, undefined) values on bottom
    if (a[field] == null) {
      return 1;
    }

    if (b[field] == null) {
      return -1;
    }

    if (typeof a[field] === "string") {
      const valueA = (a[field] as unknown) as string;
      const valueB = (b[field] as unknown) as string;
      // always sort values "unassigned" on top
      if (nameIsUnassigned(valueA)) {
        return -1;
      }

      if (nameIsUnassigned(valueB)) {
        return 1;
      }

      const parsedValueA = parse(valueA, DATE_FORMAT.US_SHORT, new Date());
      const parsedValueB = parse(valueB, DATE_FORMAT.US_SHORT, new Date());

      if (isValid(parsedValueA) && isValid(parsedValueB)) {
        if (parsedValueA.getTime() === parsedValueB.getTime()) {
          return 0;
        }

        const localSort =
          parsedValueA.getTime() > parsedValueB.getTime() ? 1 : -1;

        return direction * localSort;
      }

      return (
        direction *
        valueA.localeCompare(valueB, undefined, { sensitivity: "base" })
      );
    }

    if (typeof a[field] === "number") {
      const valueA = (a[field] as unknown) as number;
      const valueB = (b[field] as unknown) as number;
      return direction * (valueA - valueB);
    }

    if (typeof a[field] === "boolean") {
      const valueA = (a[field] as unknown) as boolean;
      const valueB = (b[field] as unknown) as boolean;
      if (valueA < valueB) {
        return -direction;
      } else if (valueA > valueB) {
        return direction;
      } else {
        return 0;
      }
    }

    return 0;
  });
}

type GenericEdge = SiteDetails | RoomDetails;

export function sortTableDataWithNestedValues(
  items: Array<GenericEdge>,
  sort: SortState
) {
  const field = sort.field as keyof GenericEdge["node"];
  if (!field) {
    return;
  }

  const direction = sort.direction === "ASC" ? 1 : -1;
  items.sort((a: GenericEdge, b: GenericEdge) => {
    // always sort empty (null, undefined) values on bottom
    if (a.node[field] == null) {
      return 1;
    }

    if (b.node[field] == null) {
      return -1;
    }

    if (typeof a.node[field] === "string") {
      const valueA = (a.node[field] as unknown) as string;
      const valueB = (b.node[field] as unknown) as string;
      // always sort values "unassigned" on top
      if (nameIsUnassigned(valueA)) {
        return -1;
      }

      if (nameIsUnassigned(valueB)) {
        return 1;
      }

      return (
        direction *
        valueA.localeCompare(valueB, undefined, { sensitivity: "base" })
      );
    }

    if (typeof a.node[field] === "number") {
      const valueA = (a.node[field] as unknown) as number;
      const valueB = (b.node[field] as unknown) as number;
      return direction * (valueA - valueB);
    }

    return 0;
  });
}

export function sortTableDataByValueModel<T>(items: Array<T>, sort: SortState) {
  const field = sort.field as keyof T;
  if (!field) {
    return;
  }

  const direction = sort.direction === "ASC" ? 1 : -1;
  items.sort((a: T, b: T) => {
    const rawValueA = _.get(a, field);
    const rawValueB = _.get(b, field);

    // always sort empty (null, undefined) values on bottom
    if (rawValueA == null) {
      return 1;
    }

    if (rawValueB == null) {
      return -1;
    }

    if (typeof rawValueA === "string") {
      const valueA = (rawValueA as unknown) as string;
      const valueB = (rawValueB as unknown) as string;
      // always sort values "unassigned" on top
      if (nameIsUnassigned(valueA)) {
        return -1;
      }

      if (nameIsUnassigned(valueB)) {
        return 1;
      }

      const parsedValueA = parse(valueA, DATE_FORMAT.US_SHORT, new Date());
      const parsedValueB = parse(valueB, DATE_FORMAT.US_SHORT, new Date());

      if (isValid(parsedValueA) && isValid(parsedValueB)) {
        if (parsedValueA.getTime() === parsedValueB.getTime()) {
          return 0;
        }

        const localSort =
          parsedValueA.getTime() > parsedValueB.getTime() ? 1 : -1;

        return direction * localSort;
      }
      return (
        direction *
        valueA.localeCompare(valueB, undefined, { sensitivity: "base" })
      );
    }

    if (typeof rawValueA === "number") {
      const valueA = (rawValueA as unknown) as number;
      const valueB = (rawValueB as unknown) as number;
      return direction * (valueA - valueB);
    }

    if (rawValueA < rawValueB) {
      return -direction;
    }

    if (rawValueA > rawValueB) {
      return direction;
    }

    return 0;
  });
}

/**
 * Utility to filter an array of table column definitions {key, title}, using the
 * passed-in filter function.
 */
export const getColumns = (
  columns: Array<string>,
  columnTranslations: Record<string, string>,
  filterFn: (column: string) => boolean = () => true
): Array<TableColumn> =>
  columns
    .filter(column => filterFn(column))
    .map(column => ({ key: column, title: columnTranslations[column] }));

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  TableBodyProps
>(
  (
    {
      loading,
      loaderClassName,
      cols,
      className,
      accommodateScrollbar = false,
      children
    },
    ref
  ) => {
    const tbodyRef = useRef<HTMLTableSectionElement>(null);
    useImperativeHandle(ref, () => tbodyRef.current as HTMLTableSectionElement);

    useEffect(() => {
      // In case of customizable tables column widths are dynamic (depends on how many columns are shown)
      // To properly align header with content for those columns both table header and table body must have the same width
      // Which is why we need to accommodate for scrollbar in these cases,
      // otherwise scrollbar will mess up table body width and hence the alignment
      if (accommodateScrollbar) {
        const setWidth = () => {
          const tbody = tbodyRef.current;
          if (tbody) {
            // add scrollbar width to table body width,
            // so columns in table header and table body are aligned correctly
            const scrollbarWidth = tbody.offsetWidth - tbody.clientWidth;
            tbody.style.width = `calc(100% + ${scrollbarWidth}px)`;
          }
        };
        setWidth();

        window.addEventListener("resize", setWidth);

        return () => {
          window.removeEventListener("resize", setWidth);
        };
      }
    }, [accommodateScrollbar]);

    const loaderClassNames = ["loader", loaderClassName]
      .filter(c => !!c)
      .join(" ");
    return (
      <tbody ref={tbodyRef} className={className}>
        {loading ? (
          <tr className={loaderClassNames}>
            <td colSpan={cols} className="text-center">
              <LoadingIndicator className="inline" />
            </td>
          </tr>
        ) : (
          <>{children}</>
        )}
      </tbody>
    );
  }
);

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  Pick<TableBulkItemRowProps, "className" | "checked" | "data-testid"> &
    React.HTMLAttributes<HTMLTableRowElement>
>(({ children, className, checked, "data-testid": testId, ...rest }, ref) => {
  const classNames = [checked ? "checked" : "", className]
    .filter(c => !!c)
    .join(" ");

  return (
    <tr {...rest} className={classNames} data-testid={testId} ref={ref}>
      {children}
    </tr>
  );
});

export const CheckTableData: React.FC<Pick<
  TableBulkItemRowProps,
  "onCheck" | "checked" | "singleSelection"
>> = ({ onCheck, checked, singleSelection = false }) => {
  return (
    <td className="bulk-item">
      {singleSelection ? (
        <input
          type="radio"
          onChange={onCheck}
          data-testid="check-item-radio"
          checked={checked}
          name="table-radio-group"
        />
      ) : (
        <input
          type="checkbox"
          onChange={onCheck}
          data-testid="check-item"
          checked={checked}
        />
      )}
    </td>
  );
};

export const TableBulkItemRow = React.forwardRef<
  HTMLTableRowElement,
  TableBulkItemRowProps
>(
  (
    {
      children,
      className,
      checked,
      onCheck,
      replaceCheckWith,
      showChecks = true,
      singleSelection,
      "data-testid": testId,
      ...rest
    },
    ref
  ) => {
    return (
      <TableRow
        {...rest}
        className={className}
        data-testid={testId}
        checked={checked}
        ref={ref}
      >
        {showChecks ? (
          replaceCheckWith ? (
            <td className="bulk-item">{replaceCheckWith}</td>
          ) : (
            <CheckTableData
              onCheck={onCheck}
              checked={checked}
              singleSelection={singleSelection}
            />
          )
        ) : null}
        {children}
      </TableRow>
    );
  }
);

export const TableHeaderItem = React.forwardRef<
  HTMLTableCellElement,
  TableHeaderProps
>(
  (
    {
      fieldName,
      leftTitle,
      title,
      rightTitle,
      className,
      dataTestId,
      sort,
      filter
    },
    ref
  ) => {
    const { t } = useTranslation();

    const sortable = !!sort;
    const filterable = !!filter;

    let sorted = false;
    let toggleSort: Function = () => {};
    if (sortable) {
      const { currentSort, onSort } = sort!;
      sorted = currentSort.field === fieldName;
      toggleSort = (field: string) => {
        let direction: SortDirection = "ASC";
        if (field === currentSort.field) {
          direction = currentSort.direction === "ASC" ? "DESC" : "ASC";
        }

        onSort({
          field,
          direction
        });
      };
    }

    return (
      <th
        className={`table-header ${className ?? ""}`}
        data-testid={dataTestId}
        ref={ref}
      >
        <span className={leftTitle || rightTitle ? "header-container" : ""}>
          {leftTitle}
          {filterable ? (
            <TableFilter fieldName={fieldName} title={title} {...filter!} />
          ) : (
            <span className={sorted ? "sorted" : ""}>{title}</span>
          )}
          {rightTitle}
          {sortable ? (
            <span data-testid="sort-icon" onClick={() => toggleSort(fieldName)}>
              <PolyIcon
                icon={`${sorted ? "arrow_up" : "arrow_up_down"}`}
                title={t("SORT")}
                className={`sort-icon clickable
                ${
                  sorted
                    ? `active ${
                        sort!.currentSort.direction === "DESC" ? "flipped" : ""
                      }`
                    : ""
                }
                ${filterable ? "mt-5" : ""}
              `}
              />
            </span>
          ) : null}
        </span>
      </th>
    );
  }
);

interface TableFilterProps extends FilterProps {
  fieldName: string;
  title: string;
}

export const TableFilter: React.FC<TableFilterProps> = ({
  fieldName,
  title,
  filterValue,
  filterOptions,
  onFilter
}) => {
  const { t } = useTranslation();
  const options = filterOptions
    .map(opt => (typeof opt === "string" ? { value: opt, text: opt } : opt))
    .sort((a, b) => (a.text > b.text ? 1 : -1));

  const text = options.find(opt => opt.value === filterValue)?.text ?? "";
  return (
    <span className="table-filter">
      <DropdownListGroup>
        <DropDownLabel value={filterValue} name={fieldName} label={title} />
        {filterValue ? (
          <span className="badge info">
            <span className="filter-value">{text}</span>
            <PolyIcon
              icon="clear_circle_outline"
              title={t("CLEAR")}
              onClick={() => onFilter(fieldName, "")}
              className="clickable pl-5"
            />
          </span>
        ) : (
          <DropdownList
            name={fieldName}
            options={options}
            onChange={value => onFilter(fieldName, value)}
            value={filterValue}
            placeholder={title}
            search
          />
        )}
      </DropdownListGroup>
    </span>
  );
};

/**
 * Renders the "global" table search input, plus optionally
 * the "Edit Table Columns" icon and dropdown.
 */
export const TableSearchEdit: React.FC<{
  className?: string;
  search?: TableSearchProps;
  edit?: TableEditProps;
}> = ({ className, search, edit }) => {
  const classes = ["table-search-edit", className, edit && "has-edit"]
    .filter(c => !!c)
    .join(" ");
  return (
    <div className={classes}>
      {search && <TableSearch {...search} />}
      {edit && <TableEdit {...edit} />}
    </div>
  );
};

export const TableSearch: React.FC<TableSearchProps> = ({
  onSearch,
  searchPlaceholder,
  evaluateOnDemand,
  debouncePeriod = 500
}) => {
  const { t } = useTranslation();
  const [urlSearchTerm, updateURLSearch, clearURLSearch] = useQueryParamString<
    "searchTerm"
  >("searchTerm");

  const input = React.useRef<HTMLInputElement>(null);
  const updateURLRef = useRef(
    _.debounce(
      (search: string) => updateURLSearch(search, evaluateOnDemand),
      debouncePeriod
    )
  );

  const clearSearch = () => {
    input.current!.value = "";
    clearURLSearch(evaluateOnDemand);
  };

  const updateDebouncedURL = updateURLRef.current;

  useEffect(() => {
    updateURLRef.current = _.debounce(
      (search: string) => updateURLSearch(search, evaluateOnDemand),
      debouncePeriod
    );
  }, [updateURLRef, updateURLSearch, debouncePeriod, evaluateOnDemand]);

  useEffect(() => {
    onSearch(urlSearchTerm || undefined);
  }, [urlSearchTerm, onSearch]);

  useEffect(() => {
    input.current!.value = urlSearchTerm ?? "";
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="table-search">
      <PolyIcon icon="search" />
      <input
        type="text"
        ref={input}
        placeholder={searchPlaceholder ?? t("SEARCH_TABLE")}
        aria-label={searchPlaceholder ?? t("SEARCH_TABLE")}
        data-testid="search-table"
        onChange={e => updateDebouncedURL(sanitizeSearchInput(e.target.value))}
        maxLength={MAX_SEARCH_CHARS}
      />
      {urlSearchTerm && <PolyIcon icon="clear_x" onClick={clearSearch} />}
    </div>
  );
};

/**
 * Renders a portion of the list of table columns in the "edit table columns" dropdown.
 * Currently, this container wraps the two lists "Visible" and "Hidden" separately
 * (thus, you cannot drag-and-drop from "Visible" to "Hidden" or vice-versa)
 */
const DragDropColumnsContainer: React.FC<{
  category: string;
  heading: string;
  columns: Array<TableColumn>;
  changeColumnStatus: (key: string) => void;
  onDragEnd: (result: DropResult) => void;
}> = ({ category, heading, columns, changeColumnStatus, onDragEnd }) => {
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={category}>
        {(provided, snapshot) => (
          <ul
            className={`m-0 p-0 ${
              snapshot.isDraggingOver ? "is-dragging" : ""
            }`}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            <li className="heading">{heading}</li>
            {columns.map(({ key, title }, index) => (
              <Draggable key={key} draggableId={key} index={index}>
                {(provided, snapshot) => (
                  <li
                    className={`
                      ${snapshot.isDragging ? "dragging" : ""}
                    `}
                    {...provided.draggableProps}
                    ref={provided.innerRef}
                  >
                    <span
                      className="mr-1"
                      data-testid="change-status"
                      onClick={() => changeColumnStatus(key)}
                    >
                      <PolyIcon
                        icon={
                          category === TABLE_COLUMN_STATE.SHOWN
                            ? "eye"
                            : "eye_slash"
                        }
                      />
                    </span>
                    <span data-testid="column">{title}</span>
                    <span
                      className="ml-1"
                      data-testid="drag-handle"
                      {...provided.dragHandleProps}
                    >
                      <PolyIcon icon="move" />
                    </span>
                  </li>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  );
};

/**
 * Renders the "Edit Table Columns" icon, as well as the dropdown list of "Visible" and "Hidden" columns.
 */
export const TableEdit: React.FC<TableEditProps> = ({
  tableName,
  shownColumns,
  hiddenColumns
}) => {
  const { t } = useTranslation();
  const editIcon = useRef<HTMLDivElement>(null);
  const self = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);
  const tablesState = useSelector((state: State) => {
    return state.userMeta?.tables ?? {};
  });
  const [setUserMeta] = useUserMeta();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        !editIcon.current?.contains(e.target as Node) &&
        !self?.current?.contains(e.target as Node)
      ) {
        setEditing(false);
      }
    };

    document.addEventListener("mousedown", handleClick, false);

    return () => {
      document.removeEventListener("mousedown", handleClick, false);
    };
  }, []);

  const changeColumnStatus = (column: string) => {
    let { SHOWN: shown, HIDDEN: hidden } = tablesState[tableName];
    shown = addOrRemove(shown, column);
    hidden = addOrRemove(hidden, column);

    setUserMeta({
      tables: {
        ...tablesState,
        [tableName]: {
          [TABLE_COLUMN_STATE.SHOWN]: shown,
          [TABLE_COLUMN_STATE.HIDDEN]: hidden
        }
      }
    });
  };

  const onDragEnd = (result: DropResult, columns: Array<TableColumn>) => {
    const { destination, source } = result;

    // do nothing if there is no destination or the destination is same as the source
    if (
      !destination ||
      (destination.droppableId === source.droppableId &&
        destination.index === source.index)
    ) {
      return;
    }

    const tableState = tablesState[tableName] ?? {};
    const columnState: keyof typeof TABLE_COLUMN_STATE = source.droppableId as TABLE_COLUMN_STATE;
    let stateColumns = Array.from(tableState[columnState] ?? []);

    // In some cases, columns we save in the state and columns displayed for user to manipulate are different
    // Because of that, source.index and destination.index are not matching what is in the state,
    // And we need to figure out actual indexes to be able to move column correctly
    const sourceIndex = stateColumns.indexOf(columns[source.index].key);
    const destinationIndex = stateColumns.indexOf(
      columns[destination.index].key
    );
    move(stateColumns, sourceIndex, destinationIndex);
    setUserMeta({
      tables: {
        ...tablesState,
        [tableName]: {
          ...tableState,
          [columnState]: stateColumns
        }
      }
    });
  };

  return (
    <>
      <div
        className="table-edit"
        data-testid="edit-table"
        onClick={() => setEditing(!editing)}
        ref={editIcon}
      >
        <PolyIcon icon="pencil" className={`${editing ? "active" : ""}`} />
      </div>
      {editing && (
        <div className="edit-dropdown" ref={self} data-testid="edit-dropdown">
          <DragDropColumnsContainer
            category={TABLE_COLUMN_STATE.SHOWN}
            heading={t("SHOWN")}
            columns={shownColumns}
            changeColumnStatus={changeColumnStatus}
            onDragEnd={result => onDragEnd(result, shownColumns)}
          />
          <DragDropColumnsContainer
            category={TABLE_COLUMN_STATE.HIDDEN}
            heading={t("HIDDEN")}
            columns={hiddenColumns}
            changeColumnStatus={changeColumnStatus}
            onDragEnd={result => onDragEnd(result, hiddenColumns)}
          />
        </div>
      )}
    </>
  );
};

export const TableHeader: React.FC<Pick<
  TableBulkItemRowProps,
  "className" | "nested"
>> = ({ children, className, nested }) => {
  return (
    <thead className={nested ? "nested" : ""} data-testid="table-header">
      <tr className={className}>{children}</tr>
    </thead>
  );
};

export const CheckTableHeader: React.FC<Pick<
  TableBulkItemRowProps,
  "onCheck" | "checked"
>> = ({ onCheck, checked }) => {
  return (
    <th className="bulk-item">
      <input
        type="checkbox"
        onChange={onCheck}
        data-testid="check-all"
        checked={checked}
        className="checkbox"
      />
    </th>
  );
};

export const ClearFiltersHeader: React.FC<Pick<
  TableBulkItemRowProps,
  "showClearFilters" | "onClearFilters"
>> = ({ showClearFilters, onClearFilters }) => {
  const { t } = useTranslation();
  return (
    <th className="clear-filters fixed-width">
      <div>
        {showClearFilters && (
          <PolyIcon
            icon="clear_circle_outline"
            title={t("CLEAR_FILTERS")}
            onClick={onClearFilters}
            className="clickable"
          />
        )}
      </div>
    </th>
  );
};

export const TableBulkItemHeader: React.FC<TableBulkItemRowProps> = ({
  children,
  className,
  checked,
  onCheck,
  replaceCheckWith,
  showChecks = true,
  onClearFilters,
  showClearFilters,
  showClearFiltersHeader
}) => {
  const renderClearFiltersHeader =
    showClearFiltersHeader !== undefined
      ? showClearFiltersHeader
      : showClearFilters !== undefined;

  return (
    <TableHeader className={className}>
      {showChecks ? (
        replaceCheckWith ? (
          <th className="bulk-item">{replaceCheckWith}</th>
        ) : (
          <CheckTableHeader onCheck={onCheck} checked={checked} />
        )
      ) : null}
      {children}
      {renderClearFiltersHeader && (
        <ClearFiltersHeader
          showClearFilters={showClearFilters}
          onClearFilters={onClearFilters}
        />
      )}
    </TableHeader>
  );
};

export const TableColumnInfoBox: React.FC<{
  getTableElements: () => {
    tableHeader: HTMLTableCellElement | null;
    tableContainer: HTMLDivElement | null;
  };
  shownColumns: string[];
}> = ({ getTableElements, shownColumns, children }) => {
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [width, setWidth] = useState(0);

  const updatePosition = useCallback(() => {
    const { tableHeader, tableContainer } = getTableElements();
    if (tableHeader && tableContainer) {
      const headerRect = tableHeader.getBoundingClientRect();
      const containerRect = tableContainer.getBoundingClientRect();
      setTop(containerRect.top);
      setLeft(headerRect.left);
      setWidth(headerRect.width);
    } else {
      setWidth(0);
    }
  }, [getTableElements]);

  useEffect(() => {
    window.addEventListener("resize", updatePosition);
    updatePosition();

    return () => window.removeEventListener("resize", updatePosition);
  }, [updatePosition]);

  useEffect(() => {
    updatePosition();
  }, [shownColumns, updatePosition]);

  return width > 0 ? (
    <div
      className="table-column-info-box"
      style={{
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`
      }}
    >
      {children}
    </div>
  ) : null;
};

export const TableVisibilitySensor: React.FC<{
  onChange: (isVisible: boolean) => void;
}> = ({ onChange }) => (
  <VisibilitySensor offset={{ bottom: -300 }} onChange={onChange}>
    <TableRow checked={false}>
      <td data-testid="Loading">
        <LoadingIndicator className="inline" />
      </td>
    </TableRow>
  </VisibilitySensor>
);
